/**
 * Pull Energy – Kern-Skripte (ohne Abhängigkeiten).
 * Web Components: <holo-tilt>, <drawer-dialog>, <cart-drawer>, <product-form>, <quantity-input>,
 * <sticky-header>, <reveal-on-scroll>. Seitenspezifisches steht in den Sections ({% javascript %}).
 */

/** Routen aus window.theme (layout/theme.liquid). */
const routes = window.theme?.routes ?? { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_url: '/cart' };

/**
 * Einfacher Ereignisbus zwischen Komponenten.
 * @typedef {'cart:updated' | 'cart:error' | 'variant:changed'} ThemeEvent
 */
export const bus = {
  /** @param {ThemeEvent} name @param {object} [detail] */
  emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  },
  /** @param {ThemeEvent} name @param {(detail: any) => void} handler */
  on(name, handler) {
    const listener = (event) => handler(event.detail);
    document.addEventListener(name, listener);
    return () => document.removeEventListener(name, listener);
  },
};

/** Section-Rendering-API: HTML einer Section als Dokument. */
export async function fetchSection(sectionId, url = window.location.pathname) {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('section_id', sectionId);
  const response = await fetch(target);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

/** Ersetzt den Inhalt von Zielelementen aus gerenderten Sections (`sections` aus /cart/*.js). */
export function applySections(sections, map) {
  for (const [sectionId, selector] of Object.entries(map)) {
    const html = sections?.[sectionId];
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source = doc.querySelector(selector);
    const target = document.querySelector(selector);
    if (source && target) target.replaceWith(source);
  }
}

/** Entprellt eine Funktion. */
export function debounce(fn, wait = 250) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/** Kurze Meldung unten (aria-live). */
export function toast(message) {
  let el = document.querySelector('.toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.append(el);
  }
  el.textContent = message;
  el.classList.add('is-visible');
  clearTimeout(el._timer);
  el._timer = setTimeout(() => el.classList.remove('is-visible'), 2800);
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

/**
 * Holo-Neigung: Karte kippt Richtung Zeiger, Glanz folgt (setzt nur CSS-Variablen, ein rAF je Frame).
 * Nur bei Maus/Trackpad und ohne „Bewegung reduzieren“.
 */
class HoloTilt extends HTMLElement {
  #frame = 0;
  #target = null;

  connectedCallback() {
    this.#target = this.querySelector('[data-holo-target]') ?? this.firstElementChild;
    if (!this.#target || reducedMotion.matches || !finePointer.matches) return;
    this.addEventListener('pointermove', this.#onMove);
    this.addEventListener('pointerleave', this.#onLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerleave', this.#onLeave);
    cancelAnimationFrame(this.#frame);
  }

  #onMove = (event) => {
    const rect = this.#target.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const style = this.#target.style;
      style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
      style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
      style.setProperty('--ry', `${((x - 0.5) * 16).toFixed(2)}deg`);
      style.setProperty('--rx', `${((0.5 - y) * 16).toFixed(2)}deg`);
      style.setProperty('--o', '1');
    });
  };

  #onLeave = () => {
    cancelAnimationFrame(this.#frame);
    const style = this.#target.style;
    style.setProperty('--rx', '0deg');
    style.setProperty('--ry', '0deg');
    style.setProperty('--o', '0');
  };
}
customElements.define('holo-tilt', HoloTilt);

/**
 * Drawer auf Basis von <dialog>: öffnen per [aria-controls] + data-drawer-open, schließen per [data-drawer-close],
 * Klick auf den Hintergrund oder Esc. Gibt den Fokus an den Auslöser zurück.
 */
class DrawerDialog extends HTMLElement {
  #dialog = null;
  #opener = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog || event.target.closest('[data-drawer-close]')) this.close();
    });
    this.#dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
  }

  get isOpen() {
    return this.#dialog?.open ?? false;
  }

  open(opener = document.activeElement) {
    if (!this.#dialog || this.#dialog.open) return;
    this.#opener = opener;
    this.#dialog.showModal();
    document.body.classList.add('has-drawer');
    requestAnimationFrame(() => this.#dialog.classList.add('is-open'));
    for (const el of document.querySelectorAll(`[aria-controls="${this.id}"]`)) el.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.#dialog?.open) return;
    this.#dialog.classList.remove('is-open');
    const done = () => {
      this.#dialog.close();
      document.body.classList.remove('has-drawer');
      this.#opener?.focus?.();
    };
    if (reducedMotion.matches) done();
    else setTimeout(done, 320);
    for (const el of document.querySelectorAll(`[aria-controls="${this.id}"]`)) el.setAttribute('aria-expanded', 'false');
  }
}
customElements.define('drawer-dialog', DrawerDialog);

// Öffner: <button aria-controls="CartDrawer" data-drawer-open>
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.getAttribute('aria-controls'));
  if (!(drawer instanceof DrawerDialog)) return;
  event.preventDefault();
  drawer.open(opener);
});

/**
 * Warenkorb-Drawer: lädt sich nach Änderungen über die Section-Rendering-API neu.
 * Erwartet Section „cart-drawer“ (snippets/cart-drawer.liquid über sections/cart-drawer.liquid) und Zähler „cart-icon-bubble“.
 */
class CartDrawer extends DrawerDialog {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onRemove);
    bus.on('cart:updated', ({ sections, openDrawer }) => {
      if (sections) this.render(sections);
      if (openDrawer && window.theme?.cartType === 'drawer') this.open();
    });
  }

  /** IDs der Sections, die nach jeder Warenkorb-Änderung neu gerendert werden. */
  static get sectionIds() {
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  render(sections) {
    applySections(sections, { 'cart-drawer': '#CartDrawerContent', 'cart-icon-bubble': '#cart-icon-bubble' });
    document.querySelector('#cart-icon-bubble .cart-count')?.classList.add('is-bumped');
  }

  #onChange = (event) => {
    const input = event.target.closest('[data-cart-line]');
    if (!input) return;
    this.#update(Number(input.dataset.cartLine), Number(input.value));
  };

  #onRemove = (event) => {
    const button = event.target.closest('[data-cart-remove]');
    if (!button) return;
    event.preventDefault();
    this.#update(Number(button.dataset.cartRemove), 0);
  };

  async #update(line, quantity) {
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: CartDrawer.sectionIds.join(','), sections_url: window.location.pathname }),
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description ?? cart.message);
      bus.emit('cart:updated', { cart, sections: cart.sections });
    } catch (error) {
      toast(error.message || window.theme?.strings?.cartError);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

/**
 * Produktformular: legt per AJAX in den Warenkorb, zeigt Fehler, öffnet den Drawer.
 * Ohne JS bleibt das normale Formular-Submit (Weiterleitung zum Warenkorb).
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  #onSubmit = async (event) => {
    if (window.theme?.cartType !== 'drawer') return;
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    const errors = this.querySelector('[data-form-error]');
    button?.classList.add('is-loading');
    button?.setAttribute('aria-disabled', 'true');
    if (errors) errors.hidden = true;
    try {
      const body = new FormData(this.form);
      body.append('sections', CartDrawer.sectionIds.join(','));
      body.append('sections_url', window.location.pathname);
      const response = await fetch(`${routes.cart_add_url}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description ?? data.message);
      bus.emit('cart:updated', { item: data, sections: data.sections, openDrawer: true });
    } catch (error) {
      if (errors) {
        errors.textContent = error.message || window.theme?.strings?.cartError;
        errors.hidden = false;
      } else {
        toast(error.message || window.theme?.strings?.cartError);
      }
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-disabled');
    }
  };
}
customElements.define('product-form', ProductForm);

/** Mengenfeld mit −/+; löst `change` am Eingabefeld aus. */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const button = event.target.closest('[data-step]');
    if (!button || !this.input) return;
    event.preventDefault();
    const step = Number(button.dataset.step);
    const min = Number(this.input.min || 0);
    const max = this.input.max ? Number(this.input.max) : Infinity;
    const next = Math.min(max, Math.max(min, Number(this.input.value || 0) + step));
    if (next === Number(this.input.value)) return;
    this.input.value = String(next);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  };
}
customElements.define('quantity-input', QuantityInput);

/** Header blendet beim Runterscrollen aus und beim Hochscrollen wieder ein. */
class StickyHeader extends HTMLElement {
  #last = 0;
  #ticking = false;

  connectedCallback() {
    this.header = this.querySelector('.site-header') ?? this;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      const hide = y > this.#last && y > 160 && !document.body.classList.contains('has-drawer');
      this.header.classList.toggle('is-hidden', hide);
      this.#last = y;
      this.#ticking = false;
    });
  };
}
customElements.define('sticky-header', StickyHeader);

/** Blendet Kinder mit .reveal beim Hineinscrollen ein (einmalig). */
class RevealOnScroll extends HTMLElement {
  connectedCallback() {
    const items = this.querySelectorAll('.reveal');
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      for (const item of items) item.classList.add('is-visible');
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          this.observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    for (const item of items) this.observer.observe(item);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
customElements.define('reveal-on-scroll', RevealOnScroll);

window.PullEnergy = { bus, fetchSection, applySections, debounce, toast };
